import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellDateTimeV8,
  CellFloatCustomV8,
  EyeColumnV8,
  toastSuccess,
} from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { Button } from 'react-bootstrap';
import { generatePath } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useMutation } from '@apollo/client';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { useHandleError } from 'shared-pages/services';
import { gqlUpdate } from '../graphql';
import { promiseModalIdocReception } from '../modal-reception-promise';

type DataGridProps = {
  data: any[];
  dataCount: number;
  initPageIndex: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  handleExport?: () => void;
  initOrderBy: string;
  onOrderChange: (order: string) => void;
  selected: any;
  setSelected: (row: any) => void;
  refetch: () => void;
  defaultReceptionDate?: Date;
  useCustomReceptionDate?: boolean;
};

export const DataGrid = forwardRef((props: DataGridProps, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    defaultReceptionDate,
    useCustomReceptionDate = true,
  } = props;

  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t } = useTranslation();
  const [updateIdoc] = useMutation(gqlUpdate);
  const { handleGraphqlError } = useHandleError('Idocs4WasteReceptionGrid');

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Idoc'), { id: row.id }),
      });
    },
    [history],
  );

  const handleReception = useCallback(
    async (row) => {
      const defaultDate = useCustomReceptionDate ? defaultReceptionDate : new Date();
      const result = await promiseModalIdocReception({
        idoc: row,
        defaultDateStorage: defaultDate,
      });
      if (!result) return;

      try {
        await updateIdoc({
          variables: {
            data: {
              id: row.id,
              dateStorage: result.dateStorage,
              receptionPersonName: appStore.userFullName,
              receptionNetWeight: Number(result.receptionNetWeight),
              idocExtras: [],
            },
          },
        });
        toastSuccess(t('Η παραλαβή καταχωρήθηκε επιτυχώς'));
        refetch();
      } catch (error) {
        handleGraphqlError('mutation-update', error);
      }
    },
    [
      appStore.userFullName,
      defaultReceptionDate,
      handleGraphqlError,
      refetch,
      t,
      updateIdoc,
      useCustomReceptionDate,
    ],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'identificationDocument',
        header: t('Αρ. Εντύπου Αναγνώρισης'),
        columnName: t('Αρ. Εντύπου Αναγνώρισης'),
        accessorKey: 'identificationDocument',
        orderByField: 'identificationDocument',
        cell: TableCellV8,
        size: 20,
      },
      {
        id: 'dateCollected',
        header: t('Ημερομηνία Συλλογής'),
        columnName: t('Ημερομηνία Συλλογής'),
        accessorKey: 'dateCollected',
        orderByField: 'dateCollected',
        cell: CellDateTimeV8,
        size: 20,
      },
      {
        id: 'collectionPointName',
        header: t('Σημείο Συλλογής'),
        columnName: t('Σημείο Συλλογής'),
        accessorKey: 'collectionPointName',
        orderByField: 'collectionPointName',
        cell: ({ row }) => (
          <div className="text-start">
            <div>{row.original.collectionPointName}</div>
            <div style={{ color: 'gray', fontSize: 'smaller' }}>{row.original.colpointAddressLine1}</div>
          </div>
        ),
        size: 25,
      },
      {
        id: 'material',
        header: t('Τύπος Αποβλήτου'),
        columnName: t('Τύπος Αποβλήτου'),
        accessorKey: 'material.name',
        orderByField: 'materialId',
        cell: ({ row }) => <span>{row.original.material?.name ?? ''}</span>,
        size: 20,
      },
      {
        id: 'netWeight',
        header: t('Βάρος σε κιλά'),
        columnName: t('Βάρος σε κιλά'),
        accessorKey: 'netWeight',
        orderByField: 'netWeight',
        cell: CellFloatCustomV8(8, 1),
        size: 12,
      },
      {
        id: 'collectorCompanyName',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'collectorCompanyName',
        orderByField: 'collectorCompanyName',
        cell: TableCellV8,
        size: 20,
      },
      {
        id: 'vehPlateNumber',
        header: t('Όχημα / Οδηγός'),
        columnName: t('Όχημα / Οδηγός'),
        accessorKey: 'vehPlateNumber',
        orderByField: 'vehPlateNumber',
        cell: ({ row }) => (
          <div className="text-start">
            {row.original.vehPlateNumber}
            <div style={{ color: 'gray', fontSize: 'smaller' }}>{row.original.driverFullName}</div>
          </div>
        ),
        size: 20,
      },
      {
        id: 'wastereceptionpointCompanyName',
        header: t('Εταιρεία Παραλαβής Αποβλήτων'),
        columnName: t('Εταιρεία Παραλαβής Αποβλήτων'),
        accessorKey: 'wastereceptionpointCompanyName',
        orderByField: 'wastereceptionpointCompanyName',
        cell: TableCellV8,
        size: 25,
      },
      {
        id: 'actions',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'actions',
        size: 15,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleReception(row.original);
            }}
          >
            {t('Παραλαβή Αποβλήτων')}
          </Button>
        ),
      },
    ],
    [handleReception, rowClicked, t],
  );

  const tableRef = useRef(null);
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text="Παρακαλώ περιμένετε...">
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="idocs4-wastereception-grid"
          appStore={appStore}
          tableStore={tableStore}
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => ({
            style: row.original.id === selected?.id ? gridRowSelectedStyle : undefined,
          })}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              const order = (col.orderByField ? col.orderByField : 'id') + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }} />
      )}
    </LoadingOverlay>
  );
});
